<template>
  <div>
    <survey-summary
      :survey="survey"
      :isSurvey="isSurvey"
      :isSimulation="isSimulation"
      :year="year"
    />
    <br />
    <div class="columns">
      <div v-if="commutesActive && !foodsActive" class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading has-text-distance">
                <b-icon icon="bicycle" />
                {{ $t('compCarbonFormsSurveyResult.annual-distance') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsSurveyResult.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span> {{ distanceTxt }} </span>
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-distance">
                <p>
                  {{ (Math.round(distance * 100) / 100) | splitNumber }}
                  <span class="is-size-6">km</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div v-if="commutesActive" class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p v-if="isPooling" class="heading has-text-carbone">
                <b-icon icon="bicycle" />
                {{
                  $t('compCarbonFormsSurveyResult.annual-emissions-carpooling')
                }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsSurveyResult.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span
                      v-html="$t('compCarbonFormsSurveyResult.emissions-txt')"
                    />
                  </template>
                </b-tooltip>
              </p>
              <p v-else class="heading has-text-carbone">
                <b-icon icon="bicycle" />
                {{ $t('compCarbonFormsSurveyResult.annual-emissions') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsSurveyResult.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span
                      v-html="$t('compCarbonFormsSurveyResult.emissions-txt')"
                    />
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-carbone">
                <p>
                  {{
                    (Math.round(commuteIntensity.intensity * 100) / 100)
                      | splitNumber
                  }}
                  &plusmn;
                  {{
                    (Math.round(commuteIntensity.uncertainty * 100) / 100)
                      | splitNumber
                  }}
                  <span class="is-size-6">{{ $t('kg.co2e') }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div v-if="foodsActive" class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading has-text-carbone2">
                <b-icon icon="cutlery" />
                {{ $t('compCarbonFormsSurveyResult.pro-annual-emissions') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsSurveyResult.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span> {{ proEmissionsTxt }} </span>
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-carbone2">
                <p>
                  {{
                    (Math.round(foodIntensity.intensity * 100) / 100)
                      | splitNumber
                  }}
                  &plusmn;
                  {{
                    (Math.round(foodIntensity.uncertainty * 100) / 100)
                      | splitNumber
                  }}
                  <span class="is-size-6">{{ $t('kg.co2e') }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div v-if="foodsActive" class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading has-text-carbone2">
                <b-icon icon="cutlery" />
                {{ $t('compCarbonFormsSurveyResult.indiv-annual-emissions') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsSurveyResult.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span
                      v-html="
                        $t('compCarbonFormsSurveyResult.indiv-emissions-txt')
                      "
                    />
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-carbone2">
                <p>
                  {{
                    (Math.round(foodIntensityIndiv.intensity * 100) / 100)
                      | splitNumber
                  }}
                  &plusmn;
                  {{
                    (Math.round(foodIntensityIndiv.uncertainty * 100) / 100)
                      | splitNumber
                  }}
                  <span class="is-size-6">{{ $t('kg.co2e') }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
    </div>
    <b-carousel
      :autoplay="false"
      indicator-style="is-lines"
      :indicator-inside="true"
    >
      <b-carousel-item>
        <ghgi-information-fr
          :isSimulation="isSimulation"
          :simulations="simulations"
          :citySize="citySize"
        />
      </b-carousel-item>
      <b-carousel-item v-if="foodsActive">
        <meals-emission-factors />
      </b-carousel-item>
      <b-carousel-item v-if="foodsActive">
        <foods-emission-factors />
      </b-carousel-item>
    </b-carousel>
  </div>
</template>

<script>
import Commute from '@/models/carbon/Commute.js'
import { useCoreStore } from '@/stores/core.js'
import Food from '@/models/carbon/Food.js'

export default {
  name: 'Result',
  components: {
    'survey-summary': () =>
      import('@/components/carbon/forms/survey/Summary.vue'),
    'foods-emission-factors': () =>
      import('@/components/graphics/FoodsEmissionFactors.vue'),
    'meals-emission-factors': () =>
      import('@/components/graphics/MealsEmissionFactors.vue'),
    'ghgi-information-fr': () =>
      import('@/components/graphics/GHGIInformationFR.vue')
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  props: {
    survey: {
      type: Object,
      default: function () {
        return {}
      }
    },
    simulations: {
      type: Array,
      default: function () {
        return []
      }
    },
    commutesActive: {
      type: Boolean,
      default: false
    },
    foodsActive: {
      type: Boolean,
      default: false
    },
    isSurvey: {
      type: Boolean,
      default: false
    },
    isSimulation: {
      type: Boolean,
      default: false
    },
    citySize: {
      type: Number,
      default: 2
    },
    year: {
      type: Number,
      default: new Date().getFullYear()
    }
  },
  computed: {
    options: function () {
      return this.coreStore.options
    },
    commuteIntensity: function () {
      let commute = Commute.createFromObj(this.survey)
      return commute.getTotalIntensity(this.options, this.year, this.citySize)
    },
    foodIntensity: function () {
      let food = Food.createFromObj(this.survey)
      return food.getCarbonIntensity(this.options, this.year)
    },
    foodIntensityIndiv: function () {
      let food = Food.createFromObj(this.survey)
      // force calculations 52 weeks * 2 meals
      return food.getCarbonIntensity(
        { NUMBER_OF_WORKED_WEEKS: { default: 52 * 2 } },
        this.year
      )
    },
    proEmissionsTxt: function () {
      let food = Food.createFromObj(this.survey)
      let txt =
        this.$t('compCarbonFormsSurveyResult.pro-emissions-txt-1') +
        food.nWorkingDay
      txt +=
        this.$t('compCarbonFormsSurveyResult.pro-emissions-txt-2') +
        food.getNumberOfWorkedWeeks(this.options, this.year)
      return txt + this.$t('compCarbonFormsSurveyResult.pro-emissions-txt-3')
    },
    distanceTxt: function () {
      let commute = Commute.createFromObj(this.survey)
      let txt =
        this.$t('compCarbonFormsSurveyResult.distance-txt-1') +
        commute.nWorkingDay
      txt +=
        this.$t('compCarbonFormsSurveyResult.distance-txt-2') +
        commute.getNumberOfWorkedWeeks(this.options, this.year)
      return txt + this.$t('compCarbonFormsSurveyResult.distance-txt-3')
    },
    isPooling: function () {
      let commute = Commute.createFromObj(this.survey)
      return commute.isPooling
    },
    distance: function () {
      let commute = Commute.createFromObj(this.survey)
      return commute.getAnnualDistance(this.options, this.year)
    }
  }
}
</script>

<style>
.has-text-distance {
  color: #935458 !important;
}
.has-text-carbone {
  color: #8e6a9e !important;
}
.has-text-carbone2 {
  color: #208dbc !important;
}
.heading .tooltip-content {
  text-transform: none;
}
</style>
